import { useEffect, useState, useRef } from "react" // 💡 useRef 추가
import { useParams, useNavigate } from "react-router-dom"
import Header from "../components/layout/Header"
import Footer from "../components/layout/Footer"
import ProductCard from "../components/product/ProductCard"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"

import { getPublicProductDetail, getPublicProducts } from "@/api/productApi"
import { addCartItem } from "@/api/cartApi"
import { useAuth } from "@/contexts/AuthContext"
import { checkWishlist, toggleWishlist } from "@/api/wishlistApi"
import { getProductReviews } from "@/api/reviewApi"

export default function ProductDetailPage() {
    const { id } = useParams()
    const navigate = useNavigate()
    const { user, isLoggedIn } = useAuth()

    // 💡 알림창 중복 호출 방지용 Ref 추가 ddd
    const alertShownRef = useRef<string | null>(null)

    const [product, setProduct] = useState<any>(null)
    const [loading, setLoading] = useState(true)
    const [mainImage, setMainImage] = useState<string>("")
    const [quantity, setQuantity] = useState(1)
    const [addingToCart, setAddingToCart] = useState(false)
    const [isWished, setIsWished] = useState(false)
    const [reviews, setReviews] = useState<any[]>([])
    const [relatedProducts, setRelatedProducts] = useState<any[]>([])
    const [selectedOption, setSelectedOption] = useState<any>(null)

    useEffect(() => {
        const fetchData = async () => {
            if (!id) return
            try {
                setLoading(true)

                // 1. 상품 상세 정보와 리뷰만 먼저 병렬 조회 (연관 상품 제외)
                const [productRes, reviewRes] = await Promise.all([
                    getPublicProductDetail(id),
                    getProductReviews(Number(id))
                ])

                const data = productRes.data || productRes
                setProduct(data)
                setMainImage(data?.thumbnailUrl || "")
                setReviews(reviewRes.data || [])

                // 분재 카테고리 알림 로직
                if (data?.categoryName === "분재" && alertShownRef.current !== id) {
                    alertShownRef.current = id;
                    setTimeout(() => {
                        alert("당일 출고 어려움, 주문일 기준으로 다음날(1일 뒤) 또는 2일 뒤 출고");
                    }, 100);
                }

                // 2. 조회된 상품의 categoryId를 바탕으로 연관 상품 개별 조회
                // alert(data?.categoryId)
                if (data?.categoryId) {
                    const relatedRes = await getPublicProducts({ categoryId: data.categoryId, size: 4 })

                    // 기존에 에러가 났던 부분 (relatedRes 처리 로직을 이 안으로 이동)
                    const items = relatedRes?.data?.items || relatedRes?.data?.data?.items || relatedRes?.data || []

                    if (Array.isArray(items)) {
                        const filtered = items.filter((p: any) => p.productId !== Number(id)).slice(0, 3)
                        setRelatedProducts(filtered)
                    }
                } else {
                    setRelatedProducts([]) // categoryId가 없을 경우 빈 배열 처리
                }

                // 3. 찜하기 여부 확인
                if (isLoggedIn) {
                    const wishRes = await checkWishlist(Number(id))
                    setIsWished(wishRes.data)
                }
            } catch (error) {
                console.error(error)
                alert("상품 정보를 불러오지 못했습니다.")
                navigate(-1)
            } finally {
                setLoading(false)
            }
        }
        fetchData()
    }, [id, navigate, isLoggedIn])

    const hasOptions = Array.isArray(product?.options) && product.options.length > 0;

    const handleQuantityChange = (type: "increase" | "decrease") => {
        if (type === "decrease" && quantity > 1) setQuantity(quantity - 1)
        else if (type === "increase") {
            if (hasOptions && !selectedOption) {
                return alert("먼저 옵션을 선택해주세요.");
            }

            const maxStock = hasOptions ? selectedOption.stockQuantity : product.stockQuantity;

            if (quantity >= maxStock) {
                return alert(`현재 구매 가능한 최대 수량은 ${maxStock}개입니다.`);
            }
            setQuantity(quantity + 1)
        }
    }

    const handleAddToCart = async () => {
        if (!isLoggedIn) { alert("로그인이 필요한 서비스입니다."); navigate("/login"); return }

        if (hasOptions && !selectedOption) {
            return alert("필수 옵션을 선택해주세요.");
        }

        try {
            setAddingToCart(true)
            await addCartItem({
                productId: Number(id),
                quantity,
                memberId: user?.memberId,
                optionId: selectedOption?.optionId
            })
            if (confirm("장바구니에 상품을 담았습니다.\n장바구니로 이동하시겠습니까?")) navigate("/cart")
        } catch (error) { alert("장바구니 담기에 실패했습니다.") }
        finally { setAddingToCart(false) }
    }

    const handleDirectBuy = () => {
        if (hasOptions && !selectedOption) {
            return alert("필수 옵션을 선택해주세요.");
        }

        const finalProductName = hasOptions
          ? `${product.productName} (옵션: ${selectedOption.name})`
          : product.productName;

        const finalPrice = hasOptions
          ? product.price + selectedOption.additionalPrice
          : product.price;

        const orderPayload = {
            state: {
                directBuyItems: [{
                    productId: product.productId,
                    productName: finalProductName,
                    price: finalPrice,
                    quantity: quantity,
                    thumbnailUrl: product.thumbnailUrl,
                    optionId: selectedOption?.optionId,
                    optionName: selectedOption?.name
                }],
                totalAmount: finalPrice * quantity,
                isGuest: !isLoggedIn
            }
        };

        if (!isLoggedIn) {
            const isGuestBuy = window.confirm("회원가입 후 결제하시면 찜하기, 주문 조회 등 다양한 혜택을 받으실 수 있습니다.\n\n비회원으로 바로 구매하시겠습니까?\n(확인: 비회원 구매 / 취소: 로그인 화면으로 이동)");
            if (isGuestBuy) navigate("/order", orderPayload);
            else navigate("/login");
            return;
        }

        navigate("/order", orderPayload);
    }

    const handleToggleWishlist = async () => {
        if (!isLoggedIn) { alert("로그인이 필요한 서비스입니다."); navigate("/login"); return }
        try {
            const res = await toggleWishlist(Number(id))
            setIsWished(res.data)
        } catch (error) { console.error("찜하기 변경 실패:", error) }
    }

    if (loading) return <div className="min-h-screen bg-slate-50 flex h-[60vh] items-center justify-center">상품 정보를 불러오는 중입니다...</div>
    if (!product) return null

    const createMarkup = (html: string) => ({ __html: html })
    const galleryImages = [product.thumbnailUrl, ...(product.imageUrls || [])]
    const averageRating = reviews.length > 0 ? (reviews.reduce((acc, cur) => acc + cur.rating, 0) / reviews.length).toFixed(1) : 0;
    const renderStars = (rating: number) => (<div className="flex text-lg text-yellow-400">{[1, 2, 3, 4, 5].map(star => (<span key={star} className={star <= rating ? "" : "text-slate-200"}>★</span>))}</div>)
    const maskName = (name: string) => (!name || name.length < 2) ? name : name.charAt(0) + "*".repeat(name.length - 1);

    const isTotallySoldOut = hasOptions
      ? product.options.every((o:any) => o.stockQuantity <= 0 || !o.isUse)
      : product.stockQuantity <= 0;

    const currentPrice = hasOptions && selectedOption
      ? product.price + selectedOption.additionalPrice
      : product.price;

    return (
      <div className="min-h-screen bg-slate-50 text-slate-900">
          <Header />
          <main className="mx-auto max-w-7xl px-4 py-8 lg:px-6">
              <div className="mb-6"><p className="text-sm text-slate-500">홈 &gt; {product.categoryName} &gt; {product.productName}</p></div>

              <section className="grid gap-8 lg:grid-cols-2">
                  <div>
                      <div className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm"><div className="aspect-square bg-slate-100 flex items-center justify-center overflow-hidden"><img src={mainImage} alt={product.productName} className="h-full w-full object-cover" /></div></div>
                      {galleryImages.length > 1 && (
                        <div className="mt-4 grid grid-cols-4 gap-3">
                            {galleryImages.map((img, idx) => (
                              <button key={idx} onClick={() => setMainImage(img)} className={`overflow-hidden rounded-2xl border bg-white shadow-sm hover:opacity-80 ${mainImage === img ? "border-slate-900 border-2" : "border-slate-200"}`}><div className="aspect-square"><img src={img} className="h-full w-full object-cover" /></div></button>
                            ))}
                        </div>
                      )}
                  </div>

                  <div className="rounded-[28px] border border-slate-200 bg-white p-6 shadow-sm lg:p-8">
                      <div className="flex flex-wrap items-center gap-2">
                          {isTotallySoldOut ? (
                            <Badge className="rounded-full bg-slate-400 text-white">품절</Badge>
                          ) : (
                            <Badge className="rounded-full bg-slate-900 text-white">판매중</Badge>
                          )}
                          {product.categoryName !== "분재" && (
                            <Badge variant="secondary" className="rounded-full bg-emerald-100 text-emerald-700">오늘출발</Badge>
                          )}
                      </div>
                      <p className="mt-4 text-sm text-slate-500">MG퀵플라워 {product.categoryName}</p>
                      <h1 className="mt-2 text-3xl font-bold leading-tight">{product.productName}</h1>

                      {reviews.length > 0 && (
                        <div className="mt-3 flex items-center gap-2 text-sm text-slate-500">
                            <span className="text-yellow-400 text-lg">★</span><span className="font-bold text-slate-700">{averageRating}</span><span>({reviews.length}개의 리뷰)</span>
                        </div>
                      )}

                      <p className="mt-3 text-sm leading-6 text-slate-500">{product.summary}</p>

                      <Card className="mt-6 rounded-3xl border-slate-200 bg-slate-50 shadow-none">
                          <CardContent className="p-6">
                              <span className="text-3xl font-bold text-slate-900">{currentPrice?.toLocaleString("ko-KR")}원</span>
                              {selectedOption?.additionalPrice > 0 && <span className="text-sm font-medium text-slate-500 ml-2">(옵션 추가금 포함)</span>}
                          </CardContent>
                      </Card>

                      <div className="mt-6 space-y-5 border-t border-slate-200 pt-6">
                          <div className="grid grid-cols-[72px_1fr] gap-4">
                              <span className="pt-1 text-sm font-semibold text-slate-900">배송</span>
                              <div className="text-sm text-slate-600">
                                  <p className="font-medium text-slate-900">
                                      {product.categoryName === "분재" ? "무료 배송" : "오늘출발 / 무료배송"}
                                  </p>
                                  <p className="mt-1 text-slate-500">
                                      {product.categoryName === "분재"
                                        ? "당일 출고 어려움, 주문일 기준으로 다음날(1일 뒤) 또는 2일 뒤 출고"
                                        : "오후 2시 이전 결제 시 당일 출고"}
                                  </p>
                              </div>
                          </div>

                          <div className="grid grid-cols-[72px_1fr] gap-4">
                              <span className="pt-3 text-sm font-semibold text-slate-900">옵션</span>
                              {hasOptions ? (
                                <Select onValueChange={(val) => {
                                    const opt = product.options.find((o: any) => o.optionId.toString() === val);
                                    setSelectedOption(opt);
                                    setQuantity(1);
                                }}>
                                    <SelectTrigger className="h-12 rounded-xl border-slate-200">
                                        <SelectValue placeholder="옵션을 선택하세요" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {product.options.filter((o: any) => o.isUse).map((opt: any) => (
                                          <SelectItem key={opt.optionId} value={opt.optionId.toString()} disabled={opt.stockQuantity <= 0}>
                                              {opt.name}
                                              {opt.additionalPrice > 0 ? ` (+${opt.additionalPrice.toLocaleString()}원)` : ''}
                                              {opt.stockQuantity <= 0 ? ' (품절)' : ''}
                                          </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                              ) : (
                                <div className="flex h-12 items-center text-sm text-slate-500">단일 옵션 상품입니다.</div>
                              )}
                          </div>
                      </div>

                      <Card className="mt-6 rounded-3xl border-slate-200 bg-white shadow-none">
                          <CardContent className="p-5">
                              <div className="flex items-center justify-between">
                                  <span className="text-sm font-medium text-slate-700">수량</span>
                                  <div className="flex items-center rounded-xl border border-slate-200 bg-slate-50">
                                      <button onClick={() => handleQuantityChange("decrease")} className="h-10 w-10 text-lg font-medium text-slate-600 hover:bg-slate-200 rounded-l-xl">-</button>
                                      <span className="flex h-10 min-w-12 items-center justify-center text-sm font-semibold text-slate-900">{quantity}</span>
                                      <button onClick={() => handleQuantityChange("increase")} className="h-10 w-10 text-lg font-medium text-slate-600 hover:bg-slate-200 rounded-r-xl">+</button>
                                  </div>
                              </div>
                              <div className="mt-5 border-t border-slate-200 pt-5">
                                  <div className="flex items-center justify-between">
                                      <span className="text-sm font-medium text-slate-700">총 상품금액</span>
                                      <span className="text-2xl font-bold text-slate-900">{(currentPrice * quantity).toLocaleString("ko-KR")}원</span>
                                  </div>
                              </div>
                          </CardContent>
                      </Card>

                      <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-3">
                          <Button variant="outline" className={`h-12 rounded-2xl border-2 transition-colors duration-200 ${isWished ? 'border-rose-500 text-rose-500 bg-rose-50 hover:bg-rose-100' : 'hover:bg-slate-50'}`} onClick={handleToggleWishlist}>
                              {isWished ? "♥ 찜완료" : "♡ 찜하기"}
                          </Button>
                          <Button className="h-12 rounded-2xl bg-slate-900 hover:bg-slate-800 text-white" onClick={handleAddToCart} disabled={addingToCart || isTotallySoldOut}>
                              {addingToCart ? "담는 중..." : "장바구니"}
                          </Button>
                          <Button className="h-12 rounded-2xl bg-emerald-600 hover:bg-emerald-700 text-white" onClick={handleDirectBuy} disabled={isTotallySoldOut}>
                              바로구매
                          </Button>
                      </div>
                  </div>
              </section>

              <section className="mt-10">
                  <Tabs defaultValue="detail" className="w-full">
                      <div className="sticky top-[88px] z-30 bg-slate-50/95 py-3 backdrop-blur">
                          <TabsList className="grid h-12 w-full grid-cols-4 rounded-2xl border border-slate-200 bg-white p-1 shadow-sm">
                              <TabsTrigger value="detail" className="rounded-xl text-sm font-medium text-slate-500 data-[state=active]:bg-slate-900 data-[state=active]:text-white">상품상세</TabsTrigger>
                              <TabsTrigger value="review" className="rounded-xl text-sm font-medium text-slate-500 data-[state=active]:bg-slate-900 data-[state=active]:text-white">
                                  리뷰 <span className="ml-1 text-xs opacity-80">{reviews.length}</span>
                              </TabsTrigger>
                              <TabsTrigger value="shipping" className="rounded-xl text-sm font-medium text-slate-500 data-[state=active]:bg-slate-900 data-[state=active]:text-white">배송안내</TabsTrigger>
                              <TabsTrigger value="refund" className="rounded-xl text-sm font-medium text-slate-500 data-[state=active]:bg-slate-900 data-[state=active]:text-white">교환/반품</TabsTrigger>
                          </TabsList>
                      </div>
                      <TabsContent value="detail" className="mt-4">
                          <Card className="rounded-[28px] border border-slate-200 shadow-sm overflow-hidden">
                              <CardContent className="p-6 lg:p-12">
                                  {product.description ? <div className="prose prose-slate max-w-none [&>img]:mx-auto [&>img]:rounded-2xl" dangerouslySetInnerHTML={createMarkup(product.description)} /> : <div className="text-center text-slate-500 py-10">상세 설명이 등록되지 않았습니다.</div>}
                              </CardContent>
                          </Card>
                      </TabsContent>

                      <TabsContent value="review" className="mt-4">
                          <Card className="rounded-[28px] border border-slate-200 shadow-sm">
                              <CardContent className="p-6 lg:p-8">
                                  <div className="flex items-end justify-between border-b border-slate-100 pb-4 mb-6">
                                      <div>
                                          <h2 className="text-2xl font-bold">고객 리뷰</h2>
                                          {reviews.length > 0 && <p className="mt-1 text-sm text-slate-500">총 {reviews.length}개의 소중한 리뷰가 있습니다.</p>}
                                      </div>
                                  </div>
                                  {reviews.length === 0 ? (
                                    <div className="py-16 text-center"><p className="text-slate-500">아직 등록된 리뷰가 없습니다.</p><p className="text-sm text-slate-400 mt-1">첫 번째 리뷰를 남겨주세요!</p></div>
                                  ) : (
                                    <div className="space-y-6">
                                        {reviews.map(review => (
                                          <div key={review.reviewId} className="border-b border-slate-100 pb-6 last:border-0 last:pb-0">
                                              <div className="flex justify-between items-start">
                                                  <div className="flex items-center gap-3">
                                                      <div className="h-10 w-10 bg-indigo-50 text-indigo-700 font-bold rounded-full flex items-center justify-center">{review.reviewerName?.charAt(0)}</div>
                                                      <div><p className="font-bold text-sm text-slate-900">{maskName(review.reviewerName)} 님</p><div className="flex items-center gap-2 mt-0.5">{renderStars(review.rating)}<span className="text-xs text-slate-400">{review.createdAt.substring(0, 10)}</span></div></div>
                                                  </div>
                                              </div>
                                              <p className="mt-4 text-sm leading-6 text-slate-700 whitespace-pre-wrap">{review.content}</p>
                                          </div>
                                        ))}
                                    </div>
                                  )}
                              </CardContent>
                          </Card>
                      </TabsContent>
                      <TabsContent value="shipping" className="mt-4"><Card className="rounded-[28px] border border-slate-200 shadow-sm"><CardContent className="p-6 lg:p-8"><h2 className="text-2xl font-bold">배송안내</h2><p className="mt-3 text-sm text-slate-500">오후 2시 이전 결제 시 당일 출고되며, 제주/도서산간 지역은 추가 배송비가 발생할 수 있습니다.</p></CardContent></Card></TabsContent>
                      <TabsContent value="refund" className="mt-4"><Card className="rounded-[28px] border border-slate-200 shadow-sm"><CardContent className="p-6 lg:p-8"><h2 className="text-2xl font-bold">교환/반품 안내</h2><p className="mt-3 text-sm text-slate-500">
                          * 생화 상품의 특성상 제작이 완료된 상품은 고객 변심에 의한 교환 및 취소가 불가능합니다. 단, 상품 불량, 파손, 오배송 등의 경우에 한하여 교환 및 재배송이 가능합니다.
                          <br/>* 관엽 상품은 식물에 문제가 있을 경우 수령 후 3일 이내에 연락 주셔야 교환이 가능합니다. (고객 관리 미흡에 의한 건 제외)
                          <br/>* 공산품이 아니므로 실제 상품은 계절 및 배송 지역에 따라 사진과 다소 차이가 있을 수 있습니다. [수도권배송] 이외의 상품은 현지 화원에서 임의 배송하므로 부소재나 화기 디자인이 변경될 수 있으나 같은 가격대 이상의 좋은 품질로 배송해 드립니다.
                          <br/>* 기타 문의사항은 고객센터(02-3786-4377)로 전화 주시면 친절하게 상담해 드립니다.
                      </p></CardContent></Card></TabsContent>
                  </Tabs>
              </section>

              <section className="mt-10">
                  <div className="mb-5 flex items-end justify-between"><div><h2 className="text-2xl font-bold">함께 보면 좋은 상품</h2></div></div>
                  <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-3">
                      {relatedProducts.length > 0 ? (
                        relatedProducts.map((item) => (
                          <ProductCard key={item.productId || item.id} product={item} />
                        ))
                      ) : (
                        <p className="text-slate-500 col-span-full">연관 상품을 불러오고 있습니다...</p>
                      )}
                  </div>
              </section>
          </main>
          <Footer />
      </div>
    )
}